// src/entry/index-main.jsx — Vite entry for index.html (homepage).
//
// Extracted from index.html's inline <script type="text/babel"> block on
// 2026-05-27 as part of the Vite spike (spike/vite-build). The body below
// is unchanged from the original — only the shim header is new.
//
// Why the shim: under Babel-standalone, all <script type="text/babel">
// blocks share global scope, so bare names like `<HeroSection />` resolve
// to whatever component file ran earlier. Under Vite ES modules, each
// module is scope-isolated; components attach to window.X on load and
// must be pulled back into local scope to be referenced as bare JSX names.

/* City vocabulary comes from the ONE registry, imported rather than read off
   window: this entry is where the ?city= deep link is resolved and where the
   feed's city ranking is computed, so it is exactly the file that must not be
   able to fall back to a private list when a script tag is late. */
import { ALL_CITIES, rankCitiesByRows, resolveCityToken } from '../../lib/city-registry.js';

/* eslint-disable no-unused-vars */
const HeroSection = window.HeroSection;
const Header = window.Header;
const EventsMapSection = window.EventsMapSection;
const Footer = window.Footer;
const TasteProfileOverlay = window.TasteProfileOverlay;
const TasteNudgeSheet = window.TasteNudgeSheet;
const VenueRailSection = window.VenueRailSection;
const SubscribeSection = window.SubscribeSection;
// glassStyle is read at component-render-time, available on window
// (GlassTokens.jsx attaches it). No need to alias.
/* eslint-enable no-unused-vars */


/* ───── HTML entity escaping for safe template insertion ───── */
function escHtml(str) {
  if (typeof str !== 'string') return String(str ?? '');
  return str.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
}

/* ───── Fade-in wrapper using IntersectionObserver ───── */
function FadeInCard({ children }) {
  const ref = React.useRef(null);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className={`fade-in-card ${visible ? 'visible' : ''}`}>
      {children}
    </div>
  );
}

/* ───── App ───── */
const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "darkMode": true,
  "accentColor": "#FF4F00",
  "glassRadius": 32,
  "heroStyle": "minimal"
}/*EDITMODE-END*/;

function App() {
  const [tweaks, setTweaks] = React.useState(() => {
    try {
      const saved = localStorage.getItem('onsumn-tweaks-v2');
      if (!saved) return TWEAK_DEFAULTS;
      const parsed = JSON.parse(saved);
      const VALID_COLORS = ['#FF4F00', '#D57800', '#FF5717', '#395542', '#7A99AC'];
      return {
        ...TWEAK_DEFAULTS,
        darkMode: typeof parsed.darkMode === 'boolean' ? parsed.darkMode : TWEAK_DEFAULTS.darkMode,
        accentColor: VALID_COLORS.includes(parsed.accentColor) ? parsed.accentColor : TWEAK_DEFAULTS.accentColor,
        glassRadius: typeof parsed.glassRadius === 'number' && parsed.glassRadius >= 0 && parsed.glassRadius <= 32 ? parsed.glassRadius : TWEAK_DEFAULTS.glassRadius,
      };
    } catch { return TWEAK_DEFAULTS; }
  });
  const [tweaksVisible, setTweaksVisible] = React.useState(false);
  // Holds a CANONICAL city name ("Mountain View") or the ALL sentinel — never
  // a display tag. The filter keys on the claim; the chip is derived from it.
  const [activeCity, setActiveCity] = React.useState(ALL_CITIES);
  // Value-first taste ask (reciprocity — docs/design/2026-07-15-ux-
  // psychology-principles.md §3): the picker NEVER blocks first paint.
  // It opens only via the deferred nudge sheet or the TUNE YOUR WAVE
  // pill (Map.jsx toolbar). Pre-change, TasteProfileOverlay mounted on
  // first visit before the user saw a single event — ask-before-give.
  const [taste, setTaste] = React.useState(() => getSavedTaste());
  const [tastePickerOpen, setTastePickerOpen] = React.useState(false);
  const [tasteNudgeVisible, setTasteNudgeVisible] = React.useState(false);

  /* Account-lite: the WHAT FOLLOWS YOU panel's RETUNE door opens the SHIPPED
     picker rather than duplicating it — one editor per datum, the UI twin of
     the one-parser-one-file doctrine. The panel lives in the footer and this
     state lives here, so one event carries the request. It is user-invoked
     only; nothing dispatches this on load, on scroll, or on a timer. */
  React.useEffect(() => {
    const open = () => setTastePickerOpen(true);
    window.addEventListener('onsumn:open-taste', open);
    return () => window.removeEventListener('onsumn:open-taste', open);
  }, []);

  const darkMode = tweaks.darkMode;
  const radius = tweaks.glassRadius;

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // ── Deferred taste ask: one-shot engagement detector ──
  // Arms only for visitors with no saved taste AND nudge lifecycle head-
  // room (once per visit, max 3 dismissals lifetime — TasteProfile.jsx).
  // Signal = EITHER a REAL-input scroll that has carried the first
  // carousel row fully above the viewport, OR a pointerdown on any event
  // card. "Real input" = a wheel/touchmove/keydown was seen within the
  // last 1.5s — programmatic scrolls (the #event- deep-link highlight
  // below, scrollToEvent's ring-pulse) fire scroll events with NO
  // preceding input, so they never qualify. After the signal, we wait
  // 600ms of scroll-idle so the sheet never lands mid-gesture.
  React.useEffect(() => {
    if (taste) return; // saved or skipped → never auto-ask
    if (typeof window.canAutoShowTasteNudge !== 'function' || !window.canAutoShowTasteNudge()) return;

    let lastInputTs = 0;
    let armed = false;   // first row scrolled past via real input
    let done = false;
    let idleTimer = null;

    const cleanup = () => {
      window.removeEventListener('wheel', noteInput);
      window.removeEventListener('touchmove', noteInput);
      window.removeEventListener('keydown', noteInput);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('pointerdown', onCardHit, true);
      if (idleTimer) clearTimeout(idleTimer);
    };

    const fire = () => {
      if (done) return;
      done = true;
      cleanup();
      setTasteNudgeVisible(true);
      if (typeof window.markTasteNudgeShown === 'function') window.markTasteNudgeShown();
    };

    const scheduleFire = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(fire, 600);
    };

    const noteInput = () => { lastInputTs = Date.now(); };

    const onScroll = () => {
      if (done) return;
      if (Date.now() - lastInputTs > 1500) return; // no recent real input → programmatic
      if (!armed) {
        // Skip the occasion rail (pinned above YOUR WAVE in season) so arming stays on the first category row.
        const firstRow = document.querySelector('.category-row:not([data-occasion-rail])');
        if (!(firstRow && firstRow.getBoundingClientRect().bottom < 0)) return;
        armed = true;
      }
      scheduleFire();
    };

    const onCardHit = (e) => {
      if (done) return;
      if (e.target && typeof e.target.closest === 'function' && e.target.closest('[data-event-slug]')) {
        scheduleFire();
      }
    };

    window.addEventListener('wheel', noteInput, { passive: true });
    window.addEventListener('touchmove', noteInput, { passive: true });
    window.addEventListener('keydown', noteInput, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('pointerdown', onCardHit, { passive: true, capture: true });
    return cleanup;
  }, [taste]);

  React.useEffect(() => {
    localStorage.setItem('onsumn-tweaks-v2', JSON.stringify(tweaks));
  }, [tweaks]);

  React.useEffect(() => {
    const TRUSTED_ORIGINS = [window.location.origin];
    const handler = (e) => {
      if (!TRUSTED_ORIGINS.includes(e.origin)) return;
      if (e.data?.type === '__activate_edit_mode') setTweaksVisible(true);
      if (e.data?.type === '__deactivate_edit_mode') setTweaksVisible(false);
    };
    window.addEventListener('message', handler);
    if (window.parent !== window) {
      window.parent.postMessage({ type: '__edit_mode_available' }, window.location.origin);
    }
    return () => window.removeEventListener('message', handler);
  }, []);

  // Header CALENDAR pill: lit while the events section shows the calendar.
  // The section owns the view (Map.jsx) and announces it via 'onsumn:view';
  // the URL seeds the first paint so a /calendar arrival lights it at once.
  const [activePage, setActivePage] = React.useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('view') === 'calendar' ? 'calendar' : 'home';
    } catch { return 'home'; }
  });
  React.useEffect(() => {
    const onView = (e) => setActivePage(e.detail === 'calendar' ? 'calendar' : 'home');
    window.addEventListener('onsumn:view', onView);
    return () => window.removeEventListener('onsumn:view', onView);
  }, []);

  // Deep-link: #event-{slug} highlights a specific event
  React.useEffect(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#event-')) return;
    const slug = hash.replace('#event-', '');
    setTimeout(() => {
      // CSS.escape mirrors Events.jsx:242 scrollToEvent — protects against
      // slug values containing reserved selector chars from breaking the
      // querySelector or matching unintended elements.
      const target = document.querySelector(`[data-event-slug="${CSS.escape(slug)}"]`);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.style.outline = '2px solid var(--international-orange)';
        target.style.outlineOffset = '4px';
        setTimeout(() => { target.style.outline = 'none'; }, 3000);
      }
    }, 1000); // Wait for events to load
  }, []);

  const updateTweak = (key, val) => {
    const next = { ...tweaks, [key]: val };
    setTweaks(next);
    if (window.parent !== window) {
      window.parent.postMessage({ type: '__edit_mode_set_keys', edits: { [key]: val } }, window.location.origin);
    }
  };

  React.useEffect(() => {
    document.documentElement.style.setProperty('--international-orange', tweaks.accentColor);
  }, [tweaks.accentColor]);

  /* Dynamic event stats for Hero */
  const { events: liveEvents, generated } = useEvents();
  const eventCount = liveEvents.length;

  /* THE city vocabulary for this page: derived from the feed, count-ordered,
     refusals excluded. One computation feeds the header pills, the footer
     column, the ?city= resolver and the hero's city count — previously four
     different notions of "which cities exist", three of them hardcoded. */
  const cityRanking = React.useMemo(() => rankCitiesByRows(liveEvents), [liveEvents]);
  const cityNames = React.useMemo(() => cityRanking.map(r => r.city), [cityRanking]);
  /* Was `new Set(liveEvents.map(e => e.city)).size`. With the authority
     refusing venues it cannot place, `null` joined that Set as a member and
     the hero — the one line on the page that asserts a NUMBER about geography
     — would have counted an unnameable city as a city. rankCitiesByRows drops
     refusals, so this counts only cities we can name. */
  const cityCount = cityRanking.length;

  /* Deep-link: ?city=X applies the city filter from footer / external links.
     Plain <a href> elsewhere, so this runs on load rather than on click.

     The gate this replaces was `window.CITIES.includes(city)` — an exact
     string match against a hardcoded 7. It rejected ?city=BERKELEY outright
     (41 live events, simply not on the list) and failed silently on any case
     difference. resolveCityToken normalizes both sides and accepts either the
     claim ("SAN FRANCISCO") or the chip ("SF"), so every link already in the
     wild still lands. It also has to WAIT: the live vocabulary is not known at
     mount, so the effect re-runs as the feed arrives and applies exactly once
     via the ref. An unresolvable token applies NOTHING — an arbitrary query
     string must never become a rendered locality. */
  const cityParamApplied = React.useRef(false);
  React.useEffect(() => {
    if (cityParamApplied.current) return;
    let token = null;
    try { token = new URLSearchParams(window.location.search).get('city'); } catch {}
    if (!token) { cityParamApplied.current = true; return; }
    const resolved = resolveCityToken(token, cityNames);
    // Not resolvable YET and no feed yet → keep waiting rather than reject.
    if (!resolved && cityNames.length === 0) return;
    cityParamApplied.current = true;
    if (resolved) setActiveCity(resolved);
  }, [cityNames]);

  const lastUpdated = React.useMemo(() => {
    if (!generated) return 'today';
    const diff = Date.now() - new Date(generated).getTime();
    const hours = Math.floor(diff / 3600000);
    if (hours < 1) return 'just now';
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return days === 1 ? 'yesterday' : `${days}d ago`;
  }, [generated]);
  // Q4 (2026-07-28): the hero sub's "updated daily" claim is earned, not
  // asserted — ONE shared rule (isFreshDaily, Map.jsx: stamp exists, parses,
  // under 36h; fail-closed on unknown). Note the homepage ships the static
  // hero (phantom-LCP fix), where HeroSection returns null — the live
  // consumer of the same rule is the events-header subtitle in Map.jsx.
  const updatedRecently = typeof window.isFreshDaily === 'function'
    ? window.isFreshDaily(generated)
    : false;

  // Night Demand Phase 0 — anonymous chaining probe. Reads the pick array
  // markInterested() already maintains; ships no UI. Fires once per device
  // per fire-week, after the feed has loaded so slugs can hydrate. Spec:
  // docs/superpowers/specs/2026-07-16-night-demand-phase0-design.md
  const nightProbeFired = React.useRef(false);
  React.useEffect(() => {
    if (nightProbeFired.current) return;
    if (!liveEvents.length) return; // wait for the feed; slugs can't hydrate without it
    nightProbeFired.current = true;
    if (typeof window.runNightDemandProbe === 'function') {
      window.runNightDemandProbe(liveEvents);
    }
  }, [liveEvents]);

  const bg = 'transparent';

  return (
    <div style={{ minHeight: '100vh', background: bg, transition: 'background 0.5s' }}>
      {/* Taste picker — opens ONLY via the nudge sheet or the toolbar
          pill; never on first paint. onComplete re-reads storage so both
          submit (taste object) and skip ({skipped:true}) land correctly. */}
      {tastePickerOpen && (
        <TasteProfileOverlay darkMode={darkMode} onComplete={() => {
          setTaste(getSavedTaste());
          setTastePickerOpen(false);
        }} />
      )}

      {/* Deferred taste invite — non-modal, dismissible, dialog band. */}
      {tasteNudgeVisible && !tastePickerOpen && typeof TasteNudgeSheet === 'function' && (
        <TasteNudgeSheet
          darkMode={darkMode}
          onOpen={() => { setTasteNudgeVisible(false); setTastePickerOpen(true); }}
          onDismiss={() => {
            setTasteNudgeVisible(false);
            if (typeof window.markTasteNudgeDismissed === 'function') window.markTasteNudgeDismissed();
          }}
        />
      )}

      <Header darkMode={darkMode} activeCity={activeCity} setActiveCity={setActiveCity}
        cities={cityNames} activePage={activePage}
        onToggleDark={() => updateTweak('darkMode', !darkMode)} radius={radius} />
      <HeroSection darkMode={darkMode} radius={radius} eventCount={eventCount} cityCount={cityCount} lastUpdated={lastUpdated} updatedRecently={updatedRecently} />
      <VenueRailSection darkMode={darkMode} radius={radius} />
      {/* WEEKLY signup — the ask STILL follows the browse (hero + ROOMS WE
          WATCH come first), but no longer at the bottom of a 5507px page.
          Moved 2026-07-31 (Marco): measured at y=4211, 76% scroll depth,
          which almost nobody reached. Sitting ABOVE #events also puts it
          outside that section's expand-on-load region. Guarded: a missing
          script tag degrades to no section, never a crash mid-page. */}
      {SubscribeSection && <SubscribeSection darkMode={darkMode} radius={radius} />}
      <EventsMapSection darkMode={darkMode} activeCity={activeCity} radius={radius} taste={taste}
        onOpenTastePicker={() => setTastePickerOpen(true)} />
      {/* The homepage has the feed, so its footer links to cities that
          actually have rows. /venue and /flows mount Footer without this prop
          and fall back to the declared nav list in lib/city-registry.js. */}
      <Footer darkMode={darkMode} radius={radius} cities={cityNames} />

      {/* Tweaks Panel */}
      <div className={`tweaks-panel ${tweaksVisible ? 'visible' : ''}`}>
        <h3>Tweaks</h3>

        <label>Color Mode</label>
        <select value={darkMode ? 'dark' : 'light'} onChange={e => updateTweak('darkMode', e.target.value === 'dark')}>
          <option value="dark">Dark Mode</option>
          <option value="light">Light Mode</option>
        </select>

        <label>Accent Color</label>
        <select value={tweaks.accentColor} onChange={e => updateTweak('accentColor', e.target.value)}>
          <option value="#FF4F00">International Orange</option>
          <option value="#D57800">Amber Gold</option>
          <option value="#FF5717">Bright Orange</option>
          <option value="#395542">Eucalyptus Green</option>
          <option value="#7A99AC">Fog Blue</option>
        </select>

        <label>Glass Radius</label>
        <input type="range" min="0" max="32" step="2" value={radius}
          onChange={e => updateTweak('glassRadius', Number(e.target.value))} />
        <div className="tweaks-val">{radius}px — {radius === 0 ? 'Brutalist sharp' : radius <= 12 ? 'Subtle' : radius <= 24 ? 'Glass' : 'Full glass'}</div>
      </div>
    </div>
  );
}

// The former bootstrap fetched /api/config to hydrate the Google Places +
// Turnstile client keys for the flyer/manual submission form. That feature
// (and /api/config) was retired 2026-07-20, so the app now renders directly.
ReactDOM.createRoot(document.getElementById('root')).render(<App />);
